// ─── La bandeja de facturas recibidas por correo ────────────────────────────
// La función de servidor `buzon-facturas` lee contabilidad@ cada 30 minutos y
// deja aquí cada factura electrónica que llega, con su PDF ya guardado en
// OneDrive. En Facturas proveedor la persona la ABRE —se carga igual que si
// soltara el XML: proveedor, número, fecha, líneas cruzadas con la OCP—, la
// compara contra el PDF y la causa; o la descarta con un motivo. Nada se causa
// solo. Tabla: public.facturas_recibidas (supabase/2026-10-29_facturas_recibidas.sql).
import { registrarEvento } from "../core/auditLog.js";
import { C } from "../core/config.js";
import { leerFacturaXML } from "../core/facturaXML.js";
import { state } from "../core/state.js";
import { cloudState, supa } from "../core/supabaseClient.js";
import { escapeHTML, fmtCOP, fmtDate } from "../core/utils.js";
import { render } from "../ui/app.js";
import { abrirFacturaLeida } from "./fprovXML.js";

const COLUMNAS = "id,cufe,tipo,numero,fecha,vence,proveedor_nit,proveedor_nombre,subtotal,iva,total,pdf_url,pdf_ruta,correo_de,correo_asunto,correo_fecha,estado,creado";
const fechaHora = (f) => { try { return new Date(f).toLocaleString("es-CO", { dateStyle: "short", timeStyle: "short" }); } catch { return f || ""; } };

export async function cargarBandeja() {
  if (!cloudState.tenantId) return;
  const b = state._bandeja = { ...(state._bandeja || {}), cargando: true };
  const [filas, cfg, estado] = await Promise.all([
    supa.from("facturas_recibidas").select(COLUMNAS).eq("tenant_id", cloudState.tenantId).eq("estado", "pendiente")
      .order("fecha", { ascending: false }).limit(200),
    supa.from("tenant_config").select("valor").eq("tenant_id", cloudState.tenantId).eq("clave", "erp:buzon_facturas").maybeSingle(),
    // ¿Ya está conectado el correo? Lo sabe la función (los secretos no bajan).
    supa.functions.invoke("buzon-facturas", { body: { accion: "estado" } }).catch(() => ({ data: null }))
  ]);
  b.filas = (filas && filas.data) || [];
  b.error = filas && filas.error ? filas.error.message : null;
  b.revision = (cfg && cfg.data && cfg.data.valor) || (estado && estado.data && estado.data.estado) || null;
  b.conexion = (estado && estado.data) || null;
  b.cargando = false;
  render();
}

export function bandejaHTML() {
  const b = state._bandeja;
  if (b === undefined) { state._bandeja = { filas: null }; cargarBandeja(); }
  const filas = (b && b.filas) || [];
  const abierta = !(b && b.cerrada);
  const con = (b && b.conexion) || null;
  const rev = (b && b.revision) || null;
  const ult = rev && rev.ultimoResultado;
  const pie = !con ? "" : !con.configurado
    ? `<div data-bandeja-sin-conexion style="font-size:10.5px;color:#92400e;">🔌 La lectura del correo ${escapeHTML(con.buzon || "")} aún no está conectada: falta la autorización de Microsoft 365 (${(con.faltan || []).filter(f => /^MS_/.test(f)).length} dato${(con.faltan || []).length === 1 ? "" : "s"} por cargar). Mientras tanto, suelta el XML abajo.</div>`
    : `<div style="display:flex;justify-content:space-between;align-items:center;gap:8px;flex-wrap:wrap;font-size:10.5px;color:#475569;">
        <span>${rev && rev.ultimoError ? `<b style="color:#b91c1c;">⚠️ Último error (${fechaHora(rev.ultimoError.fecha)}):</b> ${escapeHTML(rev.ultimoError.mensaje)}`
          : ult ? `Revisado ${fechaHora(ult.fecha)} · ${ult.nuevas} nueva${ult.nuevas === 1 ? "" : "s"}${ult.repetidas ? ` · ${ult.repetidas} ya estaba${ult.repetidas === 1 ? "" : "n"}` : ""}` : "Aún no se ha revisado el correo"}
          · se revisa cada 30 minutos</span>
        <button data-action="bandeja-revisar" style="background:white;border:1px solid #cbd5e1;padding:3px 10px;border-radius:6px;font-size:10px;font-weight:700;cursor:pointer;color:#0369a1;">🔄 Revisar ahora</button>
      </div>`;
  const lista = !b || b.filas === null || b.cargando && !filas.length
    ? `<div style="font-size:10.5px;color:${C.muted};padding:6px 0;">Cargando…</div>`
    : !filas.length
    ? `<div style="font-size:10.5px;color:${C.muted};padding:6px 0;">No hay facturas pendientes por revisar 🎉</div>`
    : `<div style="max-height:260px;overflow-y:auto;">${filas.map(f => `
        <div data-bandeja-fila="${escapeHTML(f.id)}" style="display:flex;align-items:center;gap:8px;padding:6px 0;border-top:1px solid #e0f2fe;">
          <div style="flex:1;min-width:0;">
            <div style="font-size:11.5px;font-weight:700;color:#0f172a;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${escapeHTML(f.proveedor_nombre || f.proveedor_nit || "—")} · ${escapeHTML(f.numero || "")}</div>
            <div style="font-size:10px;color:#64748b;">${fmtDate(f.fecha)}${f.vence ? ` · vence ${fmtDate(f.vence)}` : ""} · llegó ${fechaHora(f.correo_fecha || f.creado)}${f.tipo !== "factura" ? ` · <b style="color:#b45309;">${f.tipo === "nota_credito" ? "nota crédito" : "nota débito"}</b>` : ""}</div>
          </div>
          <div style="font-size:11.5px;font-weight:800;color:#0369a1;white-space:nowrap;">${fmtCOP(f.total)}</div>
          ${f.pdf_url ? `<a href="${escapeHTML(f.pdf_url)}" target="_blank" rel="noopener" style="font-size:10.5px;font-weight:700;color:#0369a1;text-decoration:none;white-space:nowrap;" title="Abrir el PDF en OneDrive">📄 PDF</a>` : ""}
          <button data-action="bandeja-abrir" data-id="${escapeHTML(f.id)}" style="background:#0E7490;color:white;border:none;padding:4px 10px;border-radius:6px;font-size:10.5px;font-weight:700;cursor:pointer;">Abrir</button>
          <button data-action="bandeja-descartar" data-id="${escapeHTML(f.id)}" title="No se va a causar (duplicada, de otra empresa, devuelta…)" style="background:white;border:1px solid #fecaca;color:#b91c1c;padding:4px 8px;border-radius:6px;font-size:10.5px;font-weight:700;cursor:pointer;">✕</button>
        </div>`).join("")}</div>`;
  return `
    <div data-bandeja style="background:white;border:1.5px solid #bae6fd;border-radius:8px;padding:10px 12px;margin-bottom:12px;">
      <div data-action="bandeja-toggle" style="display:flex;justify-content:space-between;align-items:center;cursor:pointer;">
        <div style="font-size:12.5px;font-weight:800;color:#0369a1;">📥 Facturas recibidas por correo
          <span data-bandeja-cuenta style="background:${filas.length ? "#0369a1" : "#cbd5e1"};color:white;border-radius:10px;padding:1px 8px;font-size:10.5px;margin-left:4px;">${filas.length}</span></div>
        <span style="font-size:10.5px;color:${C.muted};">${abierta ? "▲" : "▼"}</span>
      </div>
      ${abierta ? `${lista}<div style="margin-top:6px;">${pie}</div>` : ""}
    </div>`;
}

// Cuando se causa una factura que vino del correo (abierta desde aquí o con
// el mismo XML soltado a mano), sale de la bandeja.
export async function marcarCausadaEnBandeja(cufe, info) {
  if (!cufe || !cloudState.tenantId) return;
  // Sale de la lista en pantalla de una vez: la causación repinta enseguida y
  // no debe seguir mostrándola mientras la nube responde.
  if (state._bandeja && state._bandeja.filas) state._bandeja.filas = state._bandeja.filas.filter(f => f.cufe !== cufe);
  try {
    await supa.from("facturas_recibidas")
      .update({ estado: "causada", causada: { ...info, fecha: new Date().toISOString(), por: state.usuarioNombre || state.usuarioEmail || null }, actualizado: new Date().toISOString() })
      .eq("tenant_id", cloudState.tenantId).eq("cufe", cufe).eq("estado", "pendiente");
  } catch (e) { console.error("⚠️ No se pudo sacar la factura de la bandeja:", e && e.message); }
}

import { registerActions } from "../ui/actions.js";

registerActions({
  "bandeja-toggle": () => { state._bandeja = { ...(state._bandeja || {}), cerrada: !(state._bandeja && state._bandeja.cerrada) }; render(); },
  "bandeja-abrir": async (d) => {
    const fila = ((state._bandeja && state._bandeja.filas) || []).find(f => f.id === d.id);
    const { data, error } = await supa.from("facturas_recibidas").select("xml").eq("id", d.id).maybeSingle();
    if (error || !data) { alert("⚠️ No se pudo traer la factura: " + ((error && error.message) || "no está")); return; }
    let fx;
    try { fx = leerFacturaXML(data.xml); } catch (e) { alert("⚠️ " + e.message); return; }
    await abrirFacturaLeida(fx, { recibida: { id: d.id, pdf_url: fila ? fila.pdf_url : null, pdf_ruta: fila ? fila.pdf_ruta : null } });
  },
  "bandeja-descartar": async (d) => {
    const fila = ((state._bandeja && state._bandeja.filas) || []).find(f => f.id === d.id);
    const motivo = (window.prompt(`¿Por qué no se va a causar ${fila ? fila.numero : "esta factura"}? (duplicada, de otra empresa, devuelta al proveedor…)`) || "").trim();
    if (!motivo) return;
    const { error } = await supa.from("facturas_recibidas")
      .update({ estado: "descartada", descartada: { motivo, por: state.usuarioNombre || state.usuarioEmail || null, fecha: new Date().toISOString() }, actualizado: new Date().toISOString() })
      .eq("id", d.id).eq("tenant_id", cloudState.tenantId);
    if (error) { alert("⚠️ No se pudo descartar: " + error.message); return; }
    registrarEvento("facturas_recibidas", d.id, "factura_descartada", { referencia: fila ? fila.numero : null,
      detalle: { proveedor: fila ? fila.proveedor_nombre : "", total: fila ? fila.total : 0, motivo } });
    state._bandeja.filas = state._bandeja.filas.filter(f => f.id !== d.id);
    render();
  },
  "bandeja-revisar": async () => {
    state._bandeja = { ...(state._bandeja || {}), cargando: true };
    render();
    const { data, error } = await supa.functions.invoke("buzon-facturas", { body: { accion: "revisar" } });
    if (error || !data || data.ok === false) {
      alert("⚠️ No se pudo revisar el correo: " + ((data && data.error) || (error && error.message) || "sin respuesta"));
    } else if (data.configurado === false) {
      alert("🔌 La lectura del correo aún no está conectada: falta la autorización de Microsoft 365.");
    } else {
      alert(`✅ Correo revisado: ${data.nuevas} factura${data.nuevas === 1 ? "" : "s"} nueva${data.nuevas === 1 ? "" : "s"}` +
        (data.repetidas ? `, ${data.repetidas} que ya estaba${data.repetidas === 1 ? "" : "n"}` : "") +
        (data.errores && data.errores.length ? `.\n\n⚠️ ${data.errores.join("\n")}` : "."));
    }
    await cargarBandeja();
  }
});
